import React, { forwardRef, useImperativeHandle } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import SelectSingle from "react-select";

import useDirtyState from "../../../../hooks/useDirtyState";
import customStyles from "../../../../assets/select/customStyles";

const fontOptions = [
  "Arial",
  "Arial Narrow",
  "Courier New",
  "Tahoma",
  "Times New Roman",
  "Verdana",
].map((font) => ({
  value: font,
  label: (
    <div className="flex-iac settings-item" style={{ fontFamily: font }}>
      {font}
    </div>
  ),
}));

const SmartSignSettingsText = forwardRef((props, ref) => {
  const { t } = useTranslation();
  //   {
  //   free: {
  //     mode: "text",
  //     text: "FREE",
  //     font: "Arial",
  //     size: 12,
  //     color: "#00ff00",
  //     align: "center"
  //   },
  //   occupied: {
  //     mode: "text",
  //     text: "BUSY",
  //     font: "Arial",
  //     size: 12,
  //     color: "#ff0000",
  //     align: "center"
  //   }
  // }

  const state = useDirtyState(props.initialSettings);
  const link = localStorage.getItem("link");

  function colorChannels(hex) {
    const value = hex.replace("#", "");
    return [
      parseInt(value.substring(0, 2), 16),
      parseInt(value.substring(2, 4), 16),
      parseInt(value.substring(4, 6), 16),
    ];
  }

  async function saveSection(section) {
    const value = state.value[section];
    const elementId = props.initialSettings.backendId;
    const configType = value.configType;
    const displayMode = value.mode === "count" ? 0 : 1;
    const fontIds = {
      Arial: 0,
      "Arial Narrow": 1,
      "Courier New": 2,
      Tahoma: 3,
      "Times New Roman": 4,
      Verdana: 5,
    };
    const orientations = { left: 0, center: 1, right: 2 };
    const [r, g, b] = colorChannels(value.color);

    await Promise.all([
      axios.put(
        `${link}/api/TextDisplay/DisplayMode?elementId=${elementId}&configType=${configType}&mode=${displayMode}`,
        null,
      ),
      axios.put(
        `${link}/api/TextDisplay/Orientation?elementId=${elementId}&configType=${configType}&orientation=${orientations[value.align]}`,
        null,
      ),
      axios.put(
        `${link}/api/TextDisplay/Text?elementId=${elementId}&configType=${configType}`,
        value.text,
        { headers: { "Content-Type": "text/plain" } },
      ),
      axios.put(
        `${link}/api/TextDisplay/Font?elementId=${elementId}&configType=${configType}&font=${fontIds[value.font]}&fontSize=${value.size}`,
        null,
      ),
      axios.put(
        `${link}/api/TextDisplay/Color?elementId=${elementId}&configType=${configType}&r=${r}&g=${g}&b=${b}`,
        null,
      ),
    ]);
  }

  function update(section, field, value) {
    const newValue = {
      ...state.value,
      [section]: {
        ...state.value[section],
        [field]: value,
      },
    };

    state.update(newValue);

    if (props.onUpdate) {
      props.onUpdate(newValue);
    }
  }

  useImperativeHandle(ref, function () {
    return {
      getSettings: state.getValue,
      hasChanges: state.hasChanges,
      resetChanges: state.resetDirty,
      isDirty: state.hasChanges,
      save: async () => {
        if (!state.hasChanges()) return;
        await Promise.all([saveSection("free"), saveSection("occupied")]);
        state.resetDirty();
      },
      reset: () => state.update(props.initialSettings),
    };
  });

  function renderBlock(label, key) {
    const data = state.value[key];

    return (
      <div style={boxStyle}>
        <h4>{label}</h4>

        <div className="label-container">
          <label className="input-container">
            <input
              type="radio"
              className="input-element"
              checked={data.mode === "count"}
              onChange={() => update(key, "mode", "count")}
            />
            {t("smartSign.occupancyCount")}
          </label>
        </div>
        <div className="label-container">
          <label className="input-container">
            <input
              type="radio"
              className="input-element"
              checked={data.mode === "text"}
              onChange={() => update(key, "mode", "text")}
            />
            {t("allgemein.text")}
          </label>
        </div>

        <textarea
          value={data.text}
          onChange={(e) => update(key, "text", e.target.value)}
          style={{ width: "100%", marginTop: "10px" }}
        />

        <div style={rowStyle}>
          <div style={{ width: "180px" }}>
            <SelectSingle
              className="select"
              styles={customStyles}
              isSearchable={false}
              value={
                fontOptions.find((option) => option.value === data.font) ||
                fontOptions[0]
              }
              onChange={(option) => update(key, "font", option.value)}
              options={fontOptions}
              theme={(theme) => ({
                ...theme,
                borderRadius: 3,
                colors: { ...theme.colors, primary: "#20a0ff" },
              })}
            />
          </div>

          <input
            type="number"
            value={data.size}
            onChange={(e) => update(key, "size", e.target.value)}
            style={{ width: "60px" }}
          />

          <input
            type="color"
            value={data.color}
            onChange={(e) => update(key, "color", e.target.value)}
          />

          <button
            className={`align-button ab ${
              data.align === "left" ? "selected-align-button" : ""
            }`}
            title={t("allgemein.ausrichtung.Left")}
            onClick={() => update(key, "align", "left")}
          >
            <i className="fa fa-align-left"></i>
          </button>
          <button
            className={`align-button ab ${
              data.align === "center" ? "selected-align-button" : ""
            }`}
            title={t("allgemein.ausrichtung.Centered")}
            onClick={() => update(key, "align", "center")}
          >
            <i className="fa fa-align-center"></i>
          </button>
          <button
            className={`align-button ab ${
              data.align === "right" ? "selected-align-button" : ""
            }`}
            title={t("allgemein.ausrichtung.Right")}
            onClick={() => update(key, "align", "right")}
          >
            <i className="fa fa-align-right"></i>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="settings-groups">
      <div className="settings-name">{t("smartSign.textSettings")}</div>
      {renderBlock(t("allgemein.frei"), "free")}
      {renderBlock(t("allgemein.besetzt"), "occupied")}
      {/* <div>
        <label>Free Text</label>
        <input
          type="text"
          value={props.freeText}
          onChange={function (e) {
            props.onChange({
              freeText: e.target.value,
              occupiedText: props.occupiedText,
            });
          }}
        />
      </div>

      <div>
        <label>Occupied Text</label>
        <input
          type="text"
          value={props.occupiedText}
          onChange={function (e) {
            props.onChange({
              freeText: props.freeText,
              occupiedText: e.target.value,
            });
          }}
        />
      </div> */}
    </div>
  );
});

const boxStyle = {
  border: "1px solid #ccc",
  padding: "10px",
  marginBottom: "15px",
  borderRadius: "6px",
};

const rowStyle = {
  display: "flex",
  gap: "10px",
  marginTop: "10px",
  alignItems: "center",
};

export default SmartSignSettingsText;
